
import type { IAuthorizedPageObjectTreeService, IPageController } from "../";
import { BusinessException, CatchError } from "@core/error";
import { UrlUtil } from "@core/utils";
import { BasePageController } from "../base-page-controller";
import type { IResponseEvent } from "@core/data-access/model/response-event";
import { PromptTypeEnum } from "@modules/base/decorator/error/PromptType.enum";
import type { IError } from "@core/data-access/model";
import { envConfig } from "@env/env-config";

export abstract class AuthorizedPageController extends BasePageController implements IPageController {

    private readonly OBJECT_TREE: IAuthorizedPageObjectTreeService;
    private PROMPT_DEFAULT_CONFIG: any = {
        // 是否反向显示confirm和取消按钮
        reverseButtons: true,
        // 是否显示取消按钮
        showCancelButton: false,
        // 取消按钮的自定义class类
        cancelButtonClass: 'alert-login-cancel',
        // 确认按钮的自定义class类
        confirmButtonClass: 'alert-login-confirm',
        // 为按钮添加默认的swal2样式
        buttonsStyling: true,
        // 是否允许点击对话框外部来关闭对话框
        allowOutsideClick: false,
        // 是否允许按下Esc键来关闭对话框
        allowEscapeKey: false,
    }

    public rootPath: string;
    public pageContext: qpmn.main.IPageContext;

    constructor($objectTree: IAuthorizedPageObjectTreeService, $injector?: angular.auto.IInjectorService) {
        super();

        this.OBJECT_TREE = $objectTree;
        this.$injector = $injector;
    }

    /**
     * Rest请求统一错误处理
     * @param e 
     */
    // @CatchError({ title: "Request Error" })
    public onRequestFail<T extends IError>(e: IResponseEvent<T>) {
        let $translate;
        if (this.$injector) {
            $translate = this.getInjectable("$translate");
            this.PROMPT_DEFAULT_CONFIG.title = ENV_IS_CN ? "权限无效" : "Permission invalid";
            this.PROMPT_DEFAULT_CONFIG.content = ENV_IS_CN ? "去登录" : "Go to login";
            this.PROMPT_DEFAULT_CONFIG.cancelButtonText = this.getInjectable("$translate").instant("QP_ALERT_PROMPT_CANCEL");
            this.PROMPT_DEFAULT_CONFIG.confirmButtonText = this.getInjectable("$translate").instant("QP_ALERT_PROMPT_CONFIRM");
        }

        if (e != null && typeof e === "object") {
            if (e.status == 401) {
                if (ENV_IS_CN) {
                    window.qpAlertPrompt[PromptTypeEnum.INFO]("缺少身份验证", "您没有身份验证或者身份验证已过期，请单击“确定”登录并继续。", {
                        fn: () => {
                            // 记录当前页面地址并跳转至登录页
                            this.goToLogin();
                        }
                    }, undefined, this.PROMPT_DEFAULT_CONFIG);
                } else {
                    window.qpAlertPrompt[PromptTypeEnum.INFO]("Missing Authentication", "You don't have authentication or it is expired, click OK to login and go on.", {
                        fn: () => {
                            // 记录当前页面地址并跳转至登录页
                            this.goToLogin();
                        }
                    }, undefined, this.PROMPT_DEFAULT_CONFIG);
                }

            } else if (e.status == 404) {
                // 
                // throw new BusinessException("A102B001");
                // window.qpAlertPrompt[PromptTypeEnum.ERROR]("Go to login?", "You are not currently logged in or the permission is invalid", {
                //     fn: () => {
                //         // 记录当前页面地址并跳转至登录页
                //         this.goToLogin();
                //     }
                // }, undefined, this.PROMPT_DEFAULT_CONFIG);
                if (this.$injector) {
                    window.qpAlertPrompt[PromptTypeEnum.ERROR]($translate.instant("15800105"), `${$translate.instant("A102B004")} ${this.supportmail}`);
                } else {
                    if (ENV_IS_CN) {
                        window.qpAlertPrompt[PromptTypeEnum.ERROR]("系统错误", `哎呀！尝试向服务器传输数据时连接失败。请检查您的网络连接。或者，您可以刷新此页面并重试。如果问题仍然存在，请联系我们的技术支持${this.supportmail}`);
                    } else {
                        window.qpAlertPrompt[PromptTypeEnum.ERROR]("System Error", `Oops! Connection failed when you were trying to pass data to the server. Please check your network connection. Alternatively, you can refresh this page and try again. If the problem still exists, please contact our technical support ${this.supportmail}`);
                    }
                }

            } else if (e.status == 504) {
                if (this.$injector) {
                    window.qpAlertPrompt[PromptTypeEnum.WARNING]($translate.instant("A102B005"), $translate.instant("A102B006"));
                } else {
                    if (ENV_IS_CN) {
                        window.qpAlertPrompt[PromptTypeEnum.WARNING]("网络连接超时", "网络波动，导致请求超时。请稍后重试。");
                    } else {
                        window.qpAlertPrompt[PromptTypeEnum.WARNING]("Network connection timeout", "There was a network fluctuation,which caused the request to timeout. Please try again later.");
                    }
                }

            } else if (e.status == 200 && e.data?.data?.code == "104000001") {
                // 不同Partner不可访问
                if (ENV_IS_CN) {
                    window.qpAlertPrompt[PromptTypeEnum.INFO]("缺乏许可", "您没有权限，请点击[确定]重新登录。", {
                        fn: () => {
                            // 不记录当前页面地址
                            // 跳转至登录页
                            this.goToLogin(false);
                        }
                    }, undefined, this.PROMPT_DEFAULT_CONFIG);
                } else {
                    window.qpAlertPrompt[PromptTypeEnum.INFO]("Lack of permission", "You don't have permission, click [OK] to login again.", {
                        fn: () => {
                            // 不记录当前页面地址
                            // 跳转至登录页
                            this.goToLogin(false);
                        }
                    }, undefined, this.PROMPT_DEFAULT_CONFIG);
                }

            } else if (e.status == -1) {
                if (ENV_IS_CN) {
                    window.qpAlertPrompt[PromptTypeEnum.INFO]("没有互联网连接", "您的网络不可用，请检查您的数据或 Wi-Fi 连接。");
                } else {
                    window.qpAlertPrompt[PromptTypeEnum.INFO]("No internet connection", "Your network is unavailable, Check your data or Wi-Fi connection.");
                }
            } else {
                // 通用错误
                this.commonErrorHandle(e);
            }
        } else {
            this.commonErrorHandle(e);
        }
    }

    // @CatchError()
    public onRequestFailed<T extends IError>(e: IResponseEvent<T>): angular.IPromise<T> {
        let $translate = this.getInjectable("$translate");
        let $msgDialog = this.getInjectable("qpMessageDialog");
        let defer = this.getInjectable("$q").defer<T>();

        if (e != null && typeof e === "object") {
            //没有访问权限
            if (e.status == 401) {
                // 实名用户处理
                $msgDialog.info({
                    title: $translate.instant("MISSING_AUTHENTICATION_TITLE"),
                    content: $translate.instant("MISSING_AUTHENTICATION_CONTENT"),
                    buttons: [
                        "ok"
                    ]
                }).then(res => {
                    if (res == "ok") {
                        // 记录当前页面地址并跳转至登录页
                        this.goToLogin();
                    }
                })
            } else if (e.status == 404) {
                // 请求404报错处理
                $msgDialog.info({
                    title: $translate.instant("SYSTEM_ERROR_TITLE"),
                    content: $translate.instant("SYSTEM_ERROR_CONTENT", { supportmail: envConfig.supportEmail })
                })
            } else if (e.status == 504) {
                $msgDialog.info({
                    title: $translate.instant("NETWORK_CONNECTION_TIMEOUT_TITLE"),
                    content: $translate.instant("NETWORK_CONNECTION_TIMEOUT_CONTENT")
                })
            } else if (e.status == 200) {
                // 不同Partner不可访问
                if (e.data?.data?.code == "104000001") {
                    $msgDialog.info({
                        title: $translate.instant("LACKING_PERMISSION_TITLE"),
                        content: $translate.instant("LACKING_PERMISSION_CONTENT"),
                        buttons: [
                            "ok"
                        ]
                    }).then(res => {
                        if (res == "ok") {
                            // 不记录当前页面地址
                            // 跳转至登录页
                            this.goToLogin(false);
                        }
                    })
                } else {
                    defer.resolve(e.data.data);
                }
            } else if (e.status == -1) {
                $msgDialog.info({
                    title: $translate.instant("NETWORK_UNAVAILABLE_TITLE"),
                    content: $translate.instant("NETWORK_UNAVAILABLE_CONTENT")
                })
            } else {
                this.commonErrorHandle(e);
            }
        } else {
            this.commonErrorHandle(e);
        }

        return defer.promise;
    }

    /**
    * @description 检查用户页面访问权限(登录用户可访问的)
    */
    public checkAuthorization(successFunc: Function, failureFunc?: (redirectParam?: string) => void) {
        // 实名用户且未过期,则通过权限校验
        if (!this.OBJECT_TREE.isAnonymous() && !this.OBJECT_TREE.isExpire()) {
            this.authBroadcast();
            successFunc();
        } else if (!this.OBJECT_TREE.isAnonymous() && this.OBJECT_TREE.isExpire() || this.OBJECT_TREE.isAnonymous()) {
            // 匿名用户或过期的实名用户,则提示当前页面需要登录,待用户确认后前往登录页进行登录
            this.loginPrompt();
        } else {
            throw new Error("");
        }
    }

    public loginPrompt() {
        let $translate;
        if (this.$injector) {
            $translate = this.getInjectable("$translate");
            this.PROMPT_DEFAULT_CONFIG.title = ENV_IS_CN ? "权限无效" : "Permission invalid";
            this.PROMPT_DEFAULT_CONFIG.content = ENV_IS_CN ? "去登录" : "Go to login";
            this.PROMPT_DEFAULT_CONFIG.cancelButtonText = this.getInjectable("$translate").instant("QP_ALERT_PROMPT_CANCEL");
            this.PROMPT_DEFAULT_CONFIG.confirmButtonText = this.getInjectable("$translate").instant("QP_ALERT_PROMPT_CONFIRM");
        }
        // 匿名用户则提示当前页面需要登录,待用户确认后前往登录页进行登录
        // window.qpAlertPrompt[PromptTypeEnum.WARNING](this.$injector ? $translate.instant("A102B007") : "Go to login", this.$injector ? $translate.instant("A102B008") : "You are not currently logged in or the permission is invalid", {
        //     // window.qpAlertPrompt[PromptTypeEnum.WARNING]("Go to login?", "You are not currently logged in or the permission is invalid", {
        //     fn: () => {
        //         // 记录当前页面地址并跳转至登录页
        //         this.goToLogin();
        //     }
        // }, undefined, this.PROMPT_DEFAULT_CONFIG);
        if (ENV_IS_CN) {
            window.qpAlertPrompt[PromptTypeEnum.WARNING]("去登录吗？", "您当前未登录或权限无效", {
                // window.qpAlertPrompt[PromptTypeEnum.WARNING]("Go to login?", "You are not currently logged in or the permission is invalid", {
                fn: () => {
                    // 记录当前页面地址并跳转至登录页
                    this.goToLogin();
                }
            }, undefined, this.PROMPT_DEFAULT_CONFIG);
        } else {
            window.qpAlertPrompt[PromptTypeEnum.WARNING](this.$injector ? $translate.instant("A102B007") : "Go to login", this.$injector ? $translate.instant("A102B008") : "You are not currently logged in or the permission is invalid", {
                // window.qpAlertPrompt[PromptTypeEnum.WARNING]("Go to login?", "You are not currently logged in or the permission is invalid", {
                fn: () => {
                    // 记录当前页面地址并跳转至登录页
                    this.goToLogin();
                }
            }, undefined, this.PROMPT_DEFAULT_CONFIG);
        }
    }

    public goToLogin(isRecordCurrentPageHref: boolean = true) {
        let root: string;
        if (this.rootPath != undefined && this.rootPath != "") {
            root = this.rootPath;
        } else if (this.pageContext != undefined && this.pageContext.rootPath != undefined && this.pageContext.rootPath != "") {
            root = this.pageContext.rootPath
        } else {
            root = envConfig.appPath();
        }

        if (root.endsWith("/")) {
            root = root.slice(0, -1);
        }

        // 是否记录当前页面href
        if (isRecordCurrentPageHref) {
            window.location.href = root + "/partner/login/" + "?" + encodeURIComponent(location.href);
        } else {
            window.location.href = root + "/partner/login/";
        }
    }


    /**
     * @description 匿名用户提示
     * 告知用户当前页面是实名界面,需要登录后才能访问
     */
    public anonymousUserPrompt(successCallback, failHandler?, extendCfg?) {
        let _failHandler = failHandler || {};
        let _extendCfg = extendCfg || {
            // 确认按钮上的文本
            confirmButtonText: ENV_IS_CN ? "确认" : 'Confirm',
        }
        // 提示用户,当前Token失效,需要重新登录
        if (ENV_IS_CN) {
            window.qpAlertPrompt.info("", "当前页面需要登录后才能访问。", {
                fn: successCallback
            }, _failHandler, _extendCfg);
        } else {
            window.qpAlertPrompt.info("", "The current page needs to be logged in to access it.", {
                fn: successCallback
            }, _failHandler, _extendCfg);
        }
    }
}